import { HostInput, runProgram } from "idfweb-wasix";

import { buildDirectory } from "./build.ts";
import { EXECUTION_POLICIES, GUEST_USR, toolchainEnv, type Toolchain } from "./toolchain.ts";

/**
 * clangd as a guest process, speaking the Language Server Protocol over its
 * stdin and stdout.
 *
 * The arguments every run needs are in its execution policy; what depends on
 * the project, the directory with compile_commands.json, is given here. The
 * build directory is where ESP-IDF's CMake writes that file, so it exists
 * after the first configure, and clangd picks it up when it appears.
 */

export const CLANGD = `${GUEST_USR}/bin/clangd`;

export interface LanguageServer {
    /** Send one JSON-RPC message, as its JSON text. */
    send(message: string): void;
    /** Stop the server and everything it started. */
    stop(): void;
    /** The exit code; -1 when it was stopped. */
    readonly exited: Promise<number>;
}

export interface LanguageServerOptions {
    /** Each JSON-RPC message the server sends, as its JSON text. */
    onMessage(message: string): void;
    /** What the server logs on stderr. */
    onLog?: ((text: string) => void) | undefined;
}

/**
 * Start clangd for the project at `project`, a guest path, reading
 * compile_commands.json from `buildDir`, its build directory by default.
 */
export function startClangd(
    tc: Toolchain,
    project: string,
    opts: LanguageServerOptions,
    buildDir = buildDirectory(project),
): LanguageServer {
    const input = new HostInput();
    const stop = new AbortController();
    const reader = new MessageReader();
    const decoder = new TextDecoder();
    const run = runProgram({
        vfs: tc.vfs,
        program: CLANGD,
        args: [CLANGD, `--compile-commands-dir=${buildDir}`],
        // Where clangd finds clangd/config.yaml, which drops GCC's options.
        env: { ...toolchainEnv(), XDG_CONFIG_HOME: `${GUEST_USR}/share/idfweb` },
        cwd: project,
        preopens: tc.preopens,
        policies: EXECUTION_POLICIES,
        trace: tc.trace,
        stdin: input.pipe,
        signal: stop.signal,
        onStdout: (bytes) => {
            for (const message of reader.push(bytes)) opts.onMessage(message);
        },
        onStderr: (bytes) => opts.onLog?.(decoder.decode(bytes, { stream: true })),
    });
    const exited = run.then(({ exitCode, failure }) => {
        if (failure !== undefined) opts.onLog?.(`${failure.message}\n`);
        return exitCode;
    });
    return {
        send: (message) => input.write(frame(message)),
        stop: () => {
            input.close();
            stop.abort();
        },
        exited,
    };
}

const encoder = new TextEncoder();

/** One message with its header, as the base protocol has it on a stream. */
export function frame(message: string): Uint8Array {
    const body = encoder.encode(message);
    const header = encoder.encode(`Content-Length: ${body.length}\r\n\r\n`);
    const out = new Uint8Array(header.length + body.length);
    out.set(header);
    out.set(body, header.length);
    return out;
}

const HEADER_END = encoder.encode("\r\n\r\n");

/** Splits a stream of framed messages into their bodies, however it arrives in chunks. */
export class MessageReader {
    #buffer = new Uint8Array(0);
    readonly #decoder = new TextDecoder();

    push(bytes: Uint8Array): string[] {
        const joined = new Uint8Array(this.#buffer.length + bytes.length);
        joined.set(this.#buffer);
        joined.set(bytes, this.#buffer.length);
        const messages: string[] = [];
        let start = 0;
        for (;;) {
            const end = indexOf(joined, HEADER_END, start);
            if (end < 0) break;
            const header = this.#decoder.decode(joined.subarray(start, end));
            const length = /^content-length:\s*(\d+)\s*$/im.exec(header)?.[1];
            if (length === undefined) throw new Error(`no Content-Length in "${header}"`);
            const body = end + HEADER_END.length;
            if (joined.length < body + Number(length)) break;
            messages.push(this.#decoder.decode(joined.subarray(body, body + Number(length))));
            start = body + Number(length);
        }
        this.#buffer = joined.slice(start);
        return messages;
    }
}

function indexOf(haystack: Uint8Array, needle: Uint8Array, from: number): number {
    outer: for (let i = from; i <= haystack.length - needle.length; i++) {
        for (let j = 0; j < needle.length; j++) if (haystack[i + j] !== needle[j]) continue outer;
        return i;
    }
    return -1;
}
